import React from 'react';
import Canonical from '../../seo/Canonical';
import LogisticsPageHero from '../Logistics/LogisticsPageHero';
import Newsletter from '../Homepage/Newsletter';
import Footer from '../Layouts/Footer';
import './FulfilmentCentreMiltonKeynes.css';

const FulfilmentCentreMiltonKeynes: React.FC = () => {
  return (
    <div className="mk-fulfilment-page logistics-page">
      <Canonical baseUrl="https://www.pickpackpro.co.uk" />

      <LogisticsPageHero
        ariaLabel="3PL & Fulfilment Centre Milton Keynes"
        imageSrc="/Images/uk-amazon-fba-prep-centre-warehouse.webp"
        imageAlt="Pick Pack Pro fulfilment centre in Milton Keynes"
        titleLight="3PL & Fulfilment Centre Milton Keynes"
        titleAccent="Fast, Reliable UK‑Wide Shipping"
        description={
          <p>
            Searching for a fulfilment centre in Milton Keynes that combines speed, accuracy and scale? Our MK warehouse
            delivers next‑day dispatch, 99.9% pick accuracy, real‑time integrations and transparent pricing—built for
            ambitious UK brands.
          </p>
        }
        features={[
          'Next-Day UK-Wide Dispatch',
          '99.9% Pick Accuracy',
          'Real-Time Inventory Sync',
          'Transparent Pricing',
        ]}
        primaryCta={{ label: 'Get a fulfilment quote', to: '/contact-fulfillment-services' }}
      />

      <section className="mk-address-strip">
        <div className="mk-address-inner">
          <span className="mk-address-icon" aria-hidden>📍</span>
          <p>
            <strong>Unit 36 Tanners Drive, Blakelands, Milton Keynes MK14 5BN</strong> — find us easily on Google Maps
            (search "Pick Pack Pro Blakelands MK14 5BN").
          </p>
        </div>
      </section>

      <section className="mk-content-section" aria-labelledby="mk-why-title">
        <div className="mk-content-inner">
          <h2 id="mk-why-title">Why Choose a Fulfilment Centre in Milton Keynes?</h2>
          <p>
            Milton Keynes is one of the UK's most strategic logistics hubs. Located near M1 Junctions 13–14, our
            fulfilment centre in Milton Keynes provides rapid access to national carrier networks and major population
            centres. For e‑commerce brands, this translates into predictable next‑day delivery, fewer first‑mile delays,
            and dependable peak‑season performance.
          </p>
          <ul className="mk-check-list">
            <li>Fast national coverage: 24/48‑hour delivery to most UK postcodes</li>
            <li>Proximity to carrier hubs: Royal Mail, DPD, DHL, Evri, UPS</li>
            <li>Efficient inbound routes from ports, London, Birmingham and the Midlands</li>
            <li>Lower disruption risk vs. longer trunk routes—better on‑time percentages</li>
          </ul>
        </div>
      </section>

      <section className="mk-content-section mk-content-section--alt" aria-labelledby="mk-benefits-title">
        <div className="mk-content-inner">
          <h2 id="mk-benefits-title">Warehouse Benefits That Impact Conversion and Margin</h2>
          <ul className="mk-benefits-grid">
            <li>
              <strong>99.9% Pick Accuracy</strong>
              <span>Scanner‑verified processes reduce mis‑picks and returns.</span>
            </li>
            <li>
              <strong>Next‑Day Dispatch Cut‑Offs</strong>
              <span>Reliable daily cut‑offs you can market confidently.</span>
            </li>
            <li>
              <strong>Real‑Time Sync</strong>
              <span>Shopify, Amazon FBM, eBay, TikTok Shop; one stock pool, consistent SLAs.</span>
            </li>
            <li>
              <strong>Optimised Packaging</strong>
              <span>Right‑size parcels to cut postage bands; recyclable options available.</span>
            </li>
            <li>
              <strong>Transparent Pricing</strong>
              <span>Itemised pick/pack, materials, storage and labels; no hidden fees.</span>
            </li>
            <li>
              <strong>Returns Handling</strong>
              <span>Scan, grade, restock/quarantine with reason codes and clear reporting.</span>
            </li>
          </ul>
        </div>
      </section>

      <section className="mk-content-section" aria-labelledby="mk-how-title">
        <div className="mk-content-inner">
          <h2 id="mk-how-title">How Our Milton Keynes Fulfilment Works</h2>
          <ol className="mk-steps">
            <li>
              <strong>Onboarding &amp; SKU Mapping</strong>
              <span>Connect channels (read‑only), map variants, confirm packaging and SLAs.</span>
            </li>
            <li>
              <strong>Inbound &amp; QC</strong>
              <span>Send stock to MK14 5BN; we count, QC and slot inventory by demand and fragility.</span>
            </li>
            <li>
              <strong>Real‑Time Order Import</strong>
              <span>Orders flow from Shopify, Amazon, eBay, TikTok Shop with shipping prefs.</span>
            </li>
            <li>
              <strong>Pick, Pack, Presentation</strong>
              <span>Scanner‑verified picks and packaging aligned to your brand goals.</span>
            </li>
            <li>
              <strong>Smart Carrier Selection</strong>
              <span>Optimal label by destination, size and promised service level.</span>
            </li>
            <li>
              <strong>Dispatch &amp; Tracking</strong>
              <span>Multiple daily collections, tracking back to channels, proactive exceptions.</span>
            </li>
            <li>
              <strong>Returns &amp; Reporting</strong>
              <span>Graded returns, reason codes and SLA/accuracy reporting you can act on.</span>
            </li>
          </ol>
        </div>
      </section>

      <section className="mk-content-section mk-content-section--alt" aria-labelledby="mk-couriers-title">
        <div className="mk-content-inner">
          <h2 id="mk-couriers-title">Courier Partners From Our Milton Keynes Fulfilment Centre</h2>
          <p>We blend services to meet your SLA at the lowest landed cost:</p>
          <ul className="mk-check-list">
            <li><strong>Royal Mail</strong>: Tracked 24/48, Large Letter, International</li>
            <li><strong>DPD</strong>: Next‑Day with Predict, weekend and timed options</li>
            <li><strong>DHL</strong>: Domestic and international express</li>
            <li><strong>Evri</strong>: Cost‑effective tracked economy</li>
            <li><strong>UPS</strong>: International and multi‑parcel consignments</li>
          </ul>
          <p>
            Prefer a carrier? We'll configure routing rules around your preferences—or run blended logic to optimise cost
            and speed per parcel.
          </p>
        </div>
      </section>

      <section className="mk-content-section" aria-labelledby="mk-slas-title">
        <div className="mk-content-inner">
          <h2 id="mk-slas-title">SLAs and Delivery Timelines You Can Market</h2>
          <ul className="mk-check-list">
            <li><strong>Dispatch SLA</strong>: 99.9%+ on‑time within agreed cut‑offs (often 1–3pm)</li>
            <li><strong>Domestic Delivery</strong>: RM Tracked 24 (typically next working day), RM Tracked 48 (typically two days), DPD Next Day</li>
            <li><strong>International</strong>: Economy and express routes; duties‑paid (DDP) options available</li>
            <li><strong>Reporting</strong>: Clear summaries—cut‑off adherence, on‑time %, pick accuracy, returns reasons</li>
          </ul>
        </div>
      </section>

      <section className="mk-content-section mk-content-section--alt" aria-labelledby="mk-matters-title">
        <div className="mk-content-inner">
          <h2 id="mk-matters-title">Why Location Matters for UK Fulfilment</h2>
          <p>
            A fulfilment centre in Milton Keynes positions your stock close to national line‑haul networks and major
            customer clusters. Shorter first‑mile legs reduce risk of missed injections and late deliveries—improving
            conversion, reducing WISMO and protecting repeat revenue.
          </p>
          <p>
            For fast‑growing brands, MK provides the right mix of carrier access, staffing availability and infrastructure
            resilience—key to maintaining next‑day promises through peak periods.
          </p>
        </div>
      </section>

      <section className="mk-faq-section" aria-labelledby="mk-faqs-title">
        <div className="mk-content-inner">
          <h2 id="mk-faqs-title">FAQs — 3PL & Fulfilment Centre Milton Keynes</h2>
          <div className="mk-faq-list">
            <details>
              <summary>Do you offer same‑day dispatch from Milton Keynes?</summary>
              <p>Yes—subject to earlier cut‑offs and SKU profile. We'll confirm feasibility during onboarding.</p>
            </details>
            <details>
              <summary>Can I use my own branded packaging and inserts?</summary>
              <p>Absolutely. We can store and apply your branded materials and recommend right‑sizing to reduce postage bands.</p>
            </details>
            <details>
              <summary>How quickly can we go live?</summary>
              <p>Once SKUs are mapped and stock is in, many brands go live within a few working days.</p>
            </details>
            <details>
              <summary>Do you support multichannel stock control?</summary>
              <p>Yes—unify Shopify, Amazon, eBay and TikTok Shop into one stock pool with channel‑specific rules.</p>
            </details>
            <details>
              <summary>Is on‑site visiting and goods‑in guidance available?</summary>
              <p>Yes, we provide visitor parking info and goods‑in guidance; an embedded Google Map can be added if preferred.</p>
            </details>
          </div>
        </div>
      </section>

      <Newsletter />
      <Footer />
    </div>
  );
};

export default FulfilmentCentreMiltonKeynes;
